import React, {useEffect, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import Heading from "../Heading";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import LeftRail from "./LeftRail/LeftRail";
import Checkbox from "../Checkbox";
import { convertToCAD, convertToUSD } from "../../utils/CurrencyConversion";
import { StyledRedButton, StyledWhiteButton } from "./FinFormSalesSummaryPage";
import { trackFinFormBackButton, trackFinFormPageChange, trackFinFormSubmitButton, updateUserAdditionalProductTakenOffTheFair, updateUserExistingScholasticDollarBalance, updateUserNetSales, updateUserProfitInCash, updateUserProfitInProduct, updateUserProfitInScholasticDollars, updateUserSecondFairProductBonus, updateUserSpecialBonus, updateUserTotalSales } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormReviewPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [latestStep, setLatestStep] = useState<string>("");
    const [province, setProvince] = useState<string>("");
    const [isBogoFair, setIsBogoFair] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [reviewed, setReviewed] = useState<boolean>(false);
    const navigate = useNavigate();

    // Sales Summary Variables
    const [totalSales, setTotalSales] = useState<string>("0.00");
    const [taxes, setTaxes] = useState<string>("0.00");
    const [netSales, setNetSales] = useState<string>("0.00");

    // Bonus Summary Variables
    const [secondFairRate, setSecondFairRate] = useState<string>("0.00");
    const [secondFairBonus, setSecondFairBonus] = useState<string>("0.00");
    const [specialRate, setSpecialRate] = useState<string>("0.00");
    const [specialBonus, setSpecialBonus] = useState<string>("0.00");
    const [total, setTotal] = useState<string>("0.00");

    // Earnings Variables
    const [product, setProduct] = useState<string>("0.00");
    const [scholasticDollars, setScholasticDollars] = useState<string>("0.00");
    const [cash, setCash] = useState<string>("0.00");

    // Additional Product Taken Variables
    const [scholasticDollarsBalance, setScholasticDollarsBalance] = useState<string>("0.00");
    const [additionalProduct, setAdditionalProduct] = useState<string>("0.00");

    const handleBack = async () => {
        trackFinFormBackButton();
        navigate("/financial-form/rewards");
    }

    // GET Request
    useEffect(() => {
        const getFinancialFormRewards = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/review`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (!response.data.isFinancialFormAvailable) {
                            navigate("/financial-form/intro");
                        };
                        if (response.data.latestStep === "CLOSED") {
                            navigate("/financial-form/confirmation");
                        }
                        if (response.data.latestStep === "SALES" || response.data.latestStep === "SALES_SUMMARY" || response.data.latestStep === "REWARDS") {
                            navigate("/financial-form/intro");
                        }

                        const fairInfo = response.data.fair;
                        fairInfo.start = response.data.fair.startDate;
                        fairInfo.end = response.data.fair.endDate;
                        setFairInfo(fairInfo);
                        setLatestStep(response.data.latestStep);
                        setProvince(response.data.province);
                        setIsBogoFair(response.data.isBogoFair);

                        // Sales Summary
                        const sales = response.data.salesSummary;
                        setTotalSales(sales.totalSales.toFixed(2));
                        updateUserTotalSales(sales.totalSales.toFixed(2));
                        setTaxes(sales.taxes.toFixed(2));
                        setNetSales(sales.netSales.toFixed(2));
                        updateUserNetSales(sales.netSales.toFixed(2));

                        // Bonus Summary
                        const bonusSummary = response.data.bonusSummary;
                        setSecondFairRate((bonusSummary.secondFairProductRate * 100).toString());
                        setSecondFairBonus(bonusSummary.secondFairProductBonus.toFixed(2));
                        updateUserSecondFairProductBonus(bonusSummary.secondFairProductBonus.toFixed(2));
                        setSpecialRate((bonusSummary.specialRate * 100).toString());
                        setSpecialBonus(bonusSummary.specialBonus.toFixed(2));
                        updateUserSpecialBonus(bonusSummary.specialBonus.toFixed(2));
                        setTotal(bonusSummary.total.toFixed(2));

                        // Earnings
                        const earnings = response.data.profitSelection;
                        setProduct(earnings.productTakenOffTheFair.toFixed(2));
                        updateUserProfitInProduct(earnings.productTakenOffTheFair.toFixed(2));
                        setScholasticDollars(earnings.scholasticDollars.toFixed(2));
                        updateUserProfitInScholasticDollars(earnings.scholasticDollars.toFixed(2));
                        setCash(earnings.cash.toFixed(2));
                        updateUserProfitInCash(earnings.cash.toFixed(2));
                        
                        // Additional Product Taken
                        setScholasticDollarsBalance(response.data.additionalProductTaken.existingScholasticDollarBalance.toFixed(2));
                        updateUserExistingScholasticDollarBalance(response.data.additionalProductTaken.existingScholasticDollarBalance.toFixed(2));
                        setAdditionalProduct(response.data.additionalProductTaken.additionalProductTakenOffTheFair.toFixed(2));
                        updateUserAdditionalProductTakenOffTheFair(response.data.additionalProductTaken.additionalProductTakenOffTheFair.toFixed(2));
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                trackFinFormPageChange("Financial Form Review Page");
            }
        };
        getFinancialFormRewards();
    }, [navigate]);

    // Put Request
    const handleReview = async () => {
        trackFinFormSubmitButton();
        try {
            await axios.put(
                `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/review`,
                {
                    withCredentials: true,
                }
            )
            .then((response) => {
                if (response.status === 200) {
                    navigate("/financial-form/confirmation");
                }
                else {
                    handleGenericSuccess(response);
                }
            })
            .catch((err) => {
                handleGenericErrors(err);
            });
        } catch (err: any) {
            handleGenericErrors(err);
        }
    };

    // handling left rail clicks
    const handleNextLocation = (leftRailLocation: string) => {
        navigate(leftRailLocation);
    }

    return (<>
        {!isLoading && (<>
            <FairInfoBar
                language={language}
                enableSwitch={fairInfo.enableSwitch}
                fairInfoData={fairInfo}
                organizationData={fairInfo}
                pageType={"financial-form"}
                />
            <LeftRail
                language={language}
                latestStep={latestStep}
                handleNextLocation={handleNextLocation}
            />
            <StyledDiv>
                <StyledHeading>{language === "en" ? "Review and Submit" : "Réviser et soumettre"}</StyledHeading>

                {/* Sales Summary Section */}
                <StyledCard>
                    <SubHeading>{language === "en" ? "Sales Summary" : "Sommaire des ventes"}</SubHeading>
                    <TopRow>
                        <StyledLabel>
                            <StyledSubLabel className="bold">{language === "en" ? "Total Sales" : "Ventes totales"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber className="bold">{language === "en" ? `$${convertToUSD(totalSales)}` : `${convertToCAD(totalSales)} $`}</StyledNumber>
                    </TopRow>
                    <Row>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Applicable Taxes" : "Taxes applicables"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `-$${convertToUSD(taxes)}` : `-${convertToCAD(taxes)} $`}</StyledNumber>
                    </Row>
                    <Row>
                        <StyledLabel>
                            <StyledSubLabel className="bold">{language === "en" ? "Net Sales" : "Ventes nettes"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber className="bold">{language === "en" ? `$${convertToUSD(netSales)}` : `${convertToCAD(netSales)} $`}</StyledNumber>
                    </Row>
                </StyledCard>

                {/* Bonus Summary Section */}
                {(Number(secondFairBonus) !== 0 || Number(specialBonus) !== 0) && <StyledCard>
                    <SubHeading>{language === "en" ? "Bonus Summary" : "Sommaire des primes"}</SubHeading>
                    {Number(secondFairBonus) !== 0 && <StyledRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "2nd Fair Product Bonus" : "Prime pour un deuxième Festival"}</StyledSubLabel>
                            {language === "en" ? "(Must be taken as product: Net Sales x " + secondFairRate + "%)" : "(Doit être considéré comme un produit : ventes nettes x " + secondFairRate + " %)"}
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(secondFairBonus)}` : `${convertToCAD(secondFairBonus)} $`}</StyledNumber>
                    </StyledRow>}
                    {Number(specialBonus) !== 0 && <StyledRow className={Number(secondFairBonus) !== 0 && Number(specialBonus) !== 0 ? "bothBonuses" : ""}>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Special Bonus" : "Prime spéciale"}</StyledSubLabel>
                            {language === "en" ? "(Must be taken as product: Net Sales x " + specialRate + "%)": "(Doit être considéré comme un produit : ventes nettes x " + specialRate + " %)"}
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(specialBonus)}` : `${convertToCAD(specialBonus)} $`}</StyledNumber>
                    </StyledRow>}
                    {Number(secondFairBonus) !== 0 && Number(specialBonus) !== 0 && <StyledRow className="bothBonuses">
                        <p>{language === "en" ? "Total" : "Total"}</p>
                        <p>{language === "en" ? `$${convertToUSD(total)}` : `${convertToCAD(total)} $`}</p>
                    </StyledRow>}
                </StyledCard>}

                {/* Profit Selection Section */}
                <StyledCard>
                    <SubHeading>{language === "en" ? "Earnings Selection" : "Sélection des gains"}</SubHeading>
                    <TopRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Earnings in product taken off the Fair" : "Gains en produits pris au Festival"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(product)}` : `${convertToCAD(product)} $`}</StyledNumber>
                    </TopRow>
                    {!isBogoFair && <Row>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Earnings in Scholastic Dollars" : "Gains en dollars Scholastic"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(scholasticDollars)}` : `${convertToCAD(scholasticDollars)} $`}</StyledNumber>
                    </Row>}
                    {province !== "QUEBEC" && !isBogoFair && <Row>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Earnings in cash" : "Gains en argent comptant"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(cash)}` : `${convertToCAD(cash)} $`}</StyledNumber>
                    </Row>}
                </StyledCard>

                {/* Additional Product Taken Section */}
                {Number(additionalProduct) !== 0 && <StyledCard>
                    <SubHeading>{language === "en" ? "Additional Product Taken" : "Produits additionnels pris"}</SubHeading>
                    <TopRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Existing Scholastic Dollars Balance" : "Solde des dollars Scholastic existants"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `$${convertToUSD(scholasticDollarsBalance)}` : `${convertToCAD(scholasticDollarsBalance)} $`}</StyledNumber>
                    </TopRow>
                    <Row>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Additional product taken off the Fair" : "Produits additionnels pris au Festival"}</StyledSubLabel>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? `-$${convertToUSD(additionalProduct)}` : `-${convertToCAD(additionalProduct)} $`}</StyledNumber>
                    </Row>
                </StyledCard>}
                <DisclaimerSpan>
                    <Disclaimer>{language === "en" ? 'Your total Amount Due, including Delivery Fee, will be calculated when you click "Submit" to confirm your entries.' : 'Le montant total dû, qui comprend les frais de livraison, sera calculé lorsque vous cliquerez sur « Soumettre » pour confirmer les montants entrés.'}</Disclaimer>
                    <Disclaimer>
                        {language === "en" ? (
                            <>For questions or assistance with completing your Financial Form, contact Book Fairs Customer Care at 1-888-220-9942 or email <a href="mailto:custserve@scholastic.ca">custserve@scholastic.ca</a></>
                        ) : (
                            <>Si vous avez des questions ou si vous avez besoin d’aide pour remplir votre formulaire financier, contactez le Service à la clientèle au 1-800-268-3848 ou par courriel à l’adresse <a href="mailto:serviceclientele@scholastic.ca">serviceclientele@scholastic.ca</a>.</>
                        )}
                    </Disclaimer>
                </DisclaimerSpan>
                <StyledCheckbox
                    checked={reviewed}
                    onChange={()=>{reviewed ? setReviewed(false) : setReviewed(true)}}
                    label={language === "en" ? "I have reviewed the information above and accept it as correct." : "J’ai pris connaissance des informations ci-dessus et je les considère comme étant correctes."}
                ></StyledCheckbox>

                <StyledButtons>
                    <StyledWhiteButton handleClick={handleBack}>{language === "en" ? "BACK" : "Retour"}</StyledWhiteButton>
                    <StyledRedButton handleClick={handleReview} disabled={!reviewed}>{language === "en" ? "SUBMIT" : "Soumettre"}</StyledRedButton>
                </StyledButtons>
            </StyledDiv>
        </>)}
    </>);
};

export default FinFormReviewPage;

const Disclaimer = styled.p`
    margin: 0px;
    width: 100%;
    line-height: 15px;
    color: #333;
    font-family: museo-sans;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    text-align: left;
    a {
        text-decoration: underline;
    }
`;

const StyledCheckbox = styled(Checkbox)`
    display: flex;
    justify-content: flex-start;
    text-align: left;
    padding-top: 34px;
    padding-bottom: 34px;
`;

const StyledLabel = styled.label`
    display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	&.virtual {
		padding: 0px;
	}
`;

const StyledSubLabel = styled.label`
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-size: 16px;
    font-weight: 500;
    line-height: 19px;
	color: #333333;
    @media (min-width: 561px){
        white-space: pre-wrap;
    }
    &.bold {
        font-weight: 700;
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-family: museo-sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    gap: 15px;
    border-top: 1px solid #D7D7D7;
    padding-top: 12px;
    margin-top: 12px;
    &.bold {
        font-weight: 700;
    }
`;

const StyledRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-family: museo-sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    gap: 15px;
    &.bothBonuses {
		border-top: 1px solid #D7D7D7;
        padding-top: 12px;
        margin-top: 12px;
	}
`;

const TopRow = styled(Row)`
    padding-top: 0px;
    border-top: none;
`;

const StyledDiv = styled.div`
    text-align: right;
    max-width: 600px;
    margin: 0px auto;
    padding: 40px 40px 120px 40px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    height: auto;
    @media (max-width: 1080px) {
        padding-top: 90px;
    }
`;

const StyledNumber = styled.p`
    text-wrap-mode: nowrap;
    margin: auto 0px;
    &.bold {
        font-weight: 700;
    }
`;

const StyledHeading = styled(Heading)`
    text-align: left;
    max-width: 600px;
    margin: 0px auto;
    padding: 30px 0px;
    padding-bottom: 6px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
`;

const SubHeading = styled(Heading)`
    text-align: left;
    font-family: museo-sans;
    font-size: 28px;
    font-weight: 300;
    line-height: 32px;
    text-align: left;
    padding-bottom: 19px;
`;

const StyledCard = styled.div`
    text-align: left;
    max-width: 600px;
    background-color: #F5F5F5;
    margin: 26px auto;
    padding: 48px 80px;
    box-sizing: border-box;
    border-radius: 16px;
    @media (max-width: 519px) {
        padding: 48px 25px;
    }
`;

const StyledButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
`;

const DisclaimerSpan = styled.span`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 16px;
    margin-top: 34px;
`;